/**
 * Group detail provider.
 *
 * Provides read-focused group data (group, members, pending invites) and thin mutation wrappers.
 * Permission gating uses core definitions (`GroupPermissions`) and `usePermissions` from core-ui.
 */
import {
  createContext,
  useContext as useReactContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useMutation } from "@tanstack/react-query";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { Zero, PromiseWithServerResult } from "@rocicorp/zero";
import { Group } from "@corpuscomplete/core/auth/group";
import type { Invite } from "@corpuscomplete/core/auth/invite";
import { Space } from "@corpuscomplete/core/auth/space";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { Rpc } from "../../../rpc";
import { AuthUI } from "../..";
import { GroupUI } from "..";

export namespace GroupDetail {
  export type Context = {
    groupId: string;
    userId: string;
  };

  const DataContext = createContext<Context | null>(null);

  export type DataProviderProps = PropsWithChildren<{
    groupId: string;
    userId: string;
  }>;

  export function DataProvider({ children, groupId, userId }: DataProviderProps) {
    return <DataContext value={{ groupId, userId }}>{children}</DataContext>;
  }

  export function useContext(): Context {
    const context = useReactContext(DataContext);
    if (!context) {
      throw new Error("GroupDetail.useContext must be used within GroupDetail.DataProvider");
    }
    return context;
  }

  export function useGroup(): Space.Info | undefined {
    const { groupId } = useContext();
    const [group] = useQuery(queries.space.byId({ id: groupId }));
    return group;
  }

  export function useMembers(): readonly GroupUI.Member[] {
    const group = useGroup();
    return group?.group?.members ?? [];
  }

  export function usePendingInvites(): GroupUI.Invite[] {
    const { groupId } = useContext();
    const [invites] = useQuery(queries.space.pendingInvites({ groupId }));
    return useMemo(() => invites.filter(Space.isPendingInvite), [invites]);
  }

  export function usePermissions() {
    const { groupId } = useContext();
    return AuthUI.usePermissions(Space.Permissions, groupId);
  }

  export function useMyGroupGrants(): GroupUI.GrantLike[] {
    const { groupId } = useContext();
    const [allMyGrants] = useQuery(queries.accessGrant.myGrants());
    return useMemo(
      () => allMyGrants.filter((g) => g.resourceId === groupId && g.resourceType === "space"),
      [allMyGrants, groupId],
    );
  }

  export function useGrants(): { grantsByUserId: Map<string, GroupUI.GrantLike[]> } {
    const { groupId } = useContext();
    const permissions = usePermissions();
    const [memberGrantsData] = useQuery(queries.space.memberGrants({ groupId }), {
      enabled: permissions.canViewMemberGrants,
    });
    const grantsByUserId = useMemo(
      () => Space.buildGrantsByUserId(memberGrantsData),
      [memberGrantsData],
    );
    return { grantsByUserId };
  }

  export function useLoadingState(): { isLoading: boolean } {
    const { groupId } = useContext();
    const [, groupDetails] = useQuery(queries.space.byId({ id: groupId }));
    const [, invitesDetails] = useQuery(queries.space.pendingInvites({ groupId }));
    return {
      isLoading: groupDetails.type === "unknown" || invitesDetails.type === "unknown",
    };
  }

  export function useOwnerStatus(): {
    ownerCount: number;
    currentUserIsOwner: boolean;
    isLastOwner: boolean;
  } {
    const { userId } = useContext();
    const members = useMembers();
    const { grantsByUserId } = useGrants();

    return useMemo(() => {
      const ownerCount = members.filter(
        (m) => GroupUI.getMemberRootRole(grantsByUserId.get(m.userId) ?? []) === "owner",
      ).length;
      const currentMember = members.find((m) => m.userId === userId);
      const currentUserIsOwner = currentMember
        ? GroupUI.getMemberRootRole(grantsByUserId.get(currentMember.userId) ?? []) === "owner"
        : false;
      const isLastOwner = currentUserIsOwner && ownerCount === 1;
      return { ownerCount, currentUserIsOwner, isLastOwner };
    }, [members, grantsByUserId, userId]);
  }

  export function useCanManage(): boolean {
    return usePermissions().canViewMemberGrants;
  }

  export function useCurrentUserIsMember(): boolean {
    const { userId } = useContext();
    const group = useGroup();
    return Group.hasMember(group?.group, userId);
  }

  export type Mutations = {
    rename: (name: string) => PromiseWithServerResult;
    leave: () => PromiseWithServerResult;
    del: () => PromiseWithServerResult;
    createInvite: () => Promise<Invite.Created>;
    revokeInvite: (inviteId: string) => Promise<void>;
  };

  export function useMutations(): Mutations {
    const z = useZero();
    const rpc = Rpc.useRpc();
    const { groupId } = useContext();
    const createInviteMutation = useMutation(rpc.invite.create.mutationOptions());
    const revokeInviteMutation = useMutation(rpc.invite.revoke.mutationOptions());

    return useMemo(
      () => ({
        rename: (name) => z.mutate(mutators.space.rename({ id: groupId, name })),
        leave: () => z.mutate(mutators.space.leave({ id: groupId })),
        del: () => z.mutate(mutators.space.archive({ id: groupId })),
        createInvite: () => createInviteMutation.mutateAsync({ type: "space", groupId }),
        revokeInvite: (inviteId) =>
          revokeInviteMutation.mutateAsync({ type: "space", id: inviteId, groupId }),
      }),
      [z, groupId, createInviteMutation, revokeInviteMutation],
    );
  }

  export function preload(zero: Zero, groupId: string): void {
    void zero.run(queries.space.byId({ id: groupId }));
    void zero.run(queries.space.pendingInvites({ groupId }));
    void zero.run(queries.accessGrant.myGrants());
  }
}
